'use client'

import { isProductListPageLoadingAtom } from '@/feature/product-list/services/productList.store'
import { useURLParams } from '@/lib/navigation/useURLParams'
import { useSetAtom } from 'jotai'
import { usePathname, useSearchParams } from 'next/navigation'
import { Pagination } from './_components/Pagination'

/**
 * Base pagination properties
 */
export type BasePaginationProps = UiComponentProps<{
  /** The page the user is currently on */
  currentPage: number

  /** The total of pages to display in the selector */
  totalPages: number

  /** Enable SEO links generation for search engines */
  enableSeoLinks?: boolean
}>

/**
 * Requires a `urlParams` string
 */
type WithUrlParams = BasePaginationProps & {
  /** The URL parameters used for page changes */
  urlParams: string
  onPageChange?: never
}

/**
 * Requires an `onPageChange` handler
 */
type WithOnPageChange = BasePaginationProps & {
  /** Handler for changing pages */
  onPageChange: (page: number) => void
  urlParams?: never
}

/**
 * Must include either `urlParams` or `onPageChange`, but not both
 */
export type PaginationProps = WithUrlParams | WithOnPageChange

/**
 * Component that handles pagination with URL parameters or page change callbacks
 *
 * This component provides two modes of operation:
 * 1. URL-based pagination: Uses URL parameters to track page state
 * 2. Callback-based pagination: Uses an onPageChange handler for page updates

 *
 * @param props - Component props
 * @param props.currentPage - The current active page number
 * @param props.totalPages - Total number of pages available
 * @param [props.urlParams] - URL parameter key for page tracking
 * @param [props.onPageChange] - Callback handler for page changes
 *
 * @returns Rendered pagination component
 *
 * @component
 * @example
 * ```tsx
 * // Providing an URL parameter
 * <RoutePagination
 *   currentPage={1}
 *   totalPages={10}
 *   urlParams="page"
 * />
 *
 * // Providing an onPageChange handler
 * <RoutePagination
 *   currentPage={1}
 *   totalPages={10}
 *   onPageChange={(page) => handlePageChange(page)}
 * />
 * ```
 */
export function RoutePagination({
  currentPage,
  totalPages,
  urlParams,
  onPageChange,
  enableSeoLinks,
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: PaginationProps) {
  const { setURLParams } = useURLParams()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const setIsProductListPageLoading = useSetAtom(isProductListPageLoadingAtom)

  const handleOnPageChange = (page: number) => {
    if (urlParams) {
      setURLParams([{ name: urlParams, value: String(page) }])
      setIsProductListPageLoading(true) // FIXME: @team-catalog Should be handled in component using this one
    } else {
      onPageChange?.(page)
    }
  }

  const generatedSeoLinks =
    enableSeoLinks && urlParams
      ? Array.from({ length: totalPages }, (_, i) => {
          const page = i + 1
          const params = new URLSearchParams(searchParams.toString())

          if (page === 1) {
            params.delete(urlParams)
          } else {
            params.set(urlParams, String(page))
          }

          const queryString = params.toString()

          return {
            page,
            url: queryString ? `${pathname}?${queryString}` : pathname,
          }
        })
      : undefined

  return (
    <Pagination
      currentPage={currentPage}
      totalPages={totalPages}
      goToPage={handleOnPageChange}
      nextPage={() => handleOnPageChange(currentPage + 1)}
      previousPage={() => handleOnPageChange(currentPage - 1)}
      seoLinks={generatedSeoLinks}
      className={className}
      id={id}
      data-testid={dataTestId}
      aria-label={ariaLabel}
    />
  )
}
